import { useViewStyleProps } from '@euler/components/layout/View';
import { Size } from '@euler/model/common';
import { isBlurhashValid } from 'blurhash';
import { memo, useMemo } from 'react';
import { BlurhashCanvas } from 'react-blurhash';
import { Image, ImageBackground } from 'react-native';
import { AdvancedImageProps, useImage } from './AdvancedImage.shared';

const imageDimensFromLoadEvent = (_: any, url: string) => {
  return new Promise<Size>((resolve, reject) => {
    Image.getSize(
      url,
      (width, height) => {
        resolve({ width, height });
      },
      error => {
        reject(error);
      },
    );
  });
};

export const AdvancedImage = memo(
  ({
    uri,
    source,
    resize,
    blurhash,
    onLoad,
    placeholder,
    ...props
  }: AdvancedImageProps) => {
    const [style, viewProps] = useViewStyleProps(props);

    const { hash, showBlur, imageUrl, onLoaded } = useImage({
      uri,
      resize,
      blurhash,
      onLoad,
      imageDimensFromLoadEvent,
    });

    const hashData = typeof hash === 'string' ? hash : hash.blurhash;
    const hashOptions =
      typeof hash === 'string'
        ? {}
        : { width: hash.decodeWidth, height: hash.decodeHeight };

    const isValid = useMemo(
      () => showBlur && isBlurhashValid(hashData) && hashData.length === 36,
      [showBlur, hashData],
    );

    if (!imageUrl && placeholder) {
      return <>{placeholder}</>;
    }

    return (
      <ImageBackground
        {...viewProps}
        source={
          source
            ? (source as any)
            : {
                uri: imageUrl ?? undefined,
              }
        }
        css={`
          position: relative;
          overflow: hidden;
        `}
        onLoad={onLoaded as any}
        style={style as any}
      >
        {showBlur && isValid && (
          <BlurhashCanvas
            hash={hashData}
            {...hashOptions}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
            }}
          />
        )}
      </ImageBackground>
    );
  },
);

export const Img = AdvancedImage;
